import React, { FC, useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import moment from 'moment';
import { ResponsiveBar } from '@nivo/bar';
import Header from '../../components/Header';
import OrderDetailsModal from '../../components/OrderDetailsModal';
import { FilterContext } from '../../contexts/FilterContext';
import {
  fetchRevenueVarianceAnalysis,
  RevenueVarianceData,
  OrderVariance,
} from '../../helpers/plxOrdersData';
import { tokens } from '../../theme';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatNumber } from '../../helpers/numberUtils';

/**
 * `eingebettet`: ohne eigenen Seitenkopf und Außenrand — so zeigt die Sektion die Seite als Unterseite
 * /auswertungen/umsatz/diskrepanzen (P5, 27.10.2026). Ohne den Schalter unverändert.
 */
const RevenueVariancePage: FC<{ eingebettet?: boolean }> = ({ eingebettet = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<RevenueVarianceData | null>(null);
  const [loading, setLoading] = useState(true);
  // Ladefehler sichtbar machen statt endlosem Spinner (Bedienprüfung 28.10.).
  const [fehler, setFehler] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);

  // Stable date references
  const startDate = useMemo(
    () => filterState?.startDate || moment().subtract(3, 'months').toDate(),
    [filterState?.startDate],
  );
  const endDate = useMemo(() => filterState?.endDate || new Date(), [filterState?.endDate]);

  // Format dates for API
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch data
  useEffect(() => {
    // Abbruchschutz (Critical Review 28.10.): eingebettet überträgt die Filterleiste den URL-Zeitraum erst nach dem
    // Mount — ohne Flag konnte die spätere Antwort des ALTEN Zeitraums die neue überschreiben.
    let abgebrochen = false;
    const loadData = async () => {
      setLoading(true);
      setFehler(null);
      try {
        const result = await fetchRevenueVarianceAnalysis(
          formatDateForAPI(startDate),
          formatDateForAPI(endDate),
          undefined, // products filter - can be added later
          ['Upsell', 'Bezahlt'], // Only Upsell and Bezahlt orders
        );
        if (!abgebrochen) setData(result);
      } catch (error) {
        console.error('Error loading revenue variance data:', error);
        if (!abgebrochen) setFehler(error instanceof Error ? error.message : String(error));
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };

    loadData();
    return () => {
      abgebrochen = true;
    };
  }, [startDate, endDate]);

  // Handle order row click
  const handleRowClick = (rowData: unknown) => {
    const order = rowData as OrderVariance;
    setSelectedOrderNumber(order.ordernumber);
    setModalOpen(true);
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (fehler || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Die Umsatz-Diskrepanzen konnten nicht geladen werden{fehler ? ` (${fehler})` : ''}. Bitte die Seite neu laden.
      </Alert>
    );
  }

  const { summary, by_product, top_variance_orders, variance_distribution } = data;

  // KPI Cards data
  const kpiCards = [
    {
      title: 'Theoretischer Umsatz',
      value: `€${formatNumber(summary.total_theoretical, 2)}`,
      subtitle: 'Erwarteter Gesamtumsatz',
      color: colors.blueAccent[600],
    },
    {
      title: 'Tatsächlicher Umsatz',
      value: `€${formatNumber(summary.total_actual, 2)}`,
      subtitle: 'Realer Zahlungseingang',
      color: colors.greenAccent[600],
    },
    {
      title: 'Varianz',
      value: `€${formatNumber(Math.abs(summary.total_variance), 2)}`,
      subtitle: `${summary.variance_percentage.toFixed(1)}% ${summary.total_variance < 0 ? 'weniger' : 'mehr'} als erwartet`,
      color: summary.total_variance < 0 ? colors.redAccent[600] : colors.greenAccent[600],
      icon: summary.total_variance < 0 ? <TrendingDownIcon /> : <TrendingUpIcon />,
    },
    {
      title: 'Datenabdeckung',
      value: `${summary.coverage_percentage.toFixed(1)}%`,
      subtitle: `${summary.orders_with_actual_data} von ${summary.orders_with_variance} Orders`,
      color: colors.primary[600],
    },
    {
      title: 'Ø Varianz/Order',
      value: `€${formatNumber(Math.abs(summary.total_variance / summary.orders_with_variance), 2)}`,
      subtitle: 'Durchschnittliche Abweichung',
      color: colors.grey[600],
    },
  ];

  // Chart data for product variance
  const productChartData = by_product.slice(0, 10).map((p) => ({
    product: p.product_name.length > 20 ? p.product_name.substring(0, 20) + '...' : p.product_name,
    variance: Math.abs(parseFloat(p.variance)),
    varianceColor: parseFloat(p.variance) < 0 ? colors.redAccent[500] : colors.greenAccent[500],
  }));

  // Chart data for distribution
  const distributionChartData = variance_distribution.map((d) => ({
    bucket: d.variance_bucket,
    count: d.order_count,
  }));

  return (
    <Box m={eingebettet ? 0 : '20px'}>
      {!eingebettet && (
        <Header
          title="PLX Umsatz-Diskrepanzanalyse"
          subtitle="Analyse der Abweichungen zwischen theoretischem und tatsächlichem Umsatz"
        />
      )}

      {/* Info Box */}
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          Diese Analyse vergleicht den <strong>theoretischen Umsatz</strong> (basierend auf
          Produktpreisen und Laufzeit) mit dem <strong>tatsächlichen Umsatz</strong> (zahlung_brutto
          aus PLX). Fokus auf Upsell und Bezahlt Orders. Negative Varianz = weniger Einnahmen als
          erwartet (Chargebacks, Stornos). Positive Varianz = mehr Einnahmen als erwartet.
          <br />
          <br />
          <strong>Filterung:</strong> Testbestellungen (PLZ 53119, Nachname Streit) und Varianzen
          mit kürzlich fehlgeschlagenen Zahlungen (&lt;14 Tage) werden ausgeschlossen, da PLX Zeit
          zur Aktualisierung des Umsatzfeldes benötigt.
        </Typography>
      </Alert>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color={colors.grey[100]} gutterBottom>
                    {card.title}
                  </Typography>
                  {card.icon}
                </Box>
                <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold', mb: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color={colors.grey[300]}>
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Product Variance Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Varianz nach Produkt (Top 10)</Typography>
                <Tooltip title="Zeigt die 10 Produkte mit der größten absoluten Abweichung">
                  <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[300] }} />
                </Tooltip>
              </Box>
              <Box height="300px">
                <ResponsiveBar
                  data={productChartData}
                  keys={['variance']}
                  indexBy="product"
                  margin={{ top: 10, right: 30, bottom: 80, left: 70 }}
                  padding={0.3}
                  colors={productChartData.map((d) => d.varianceColor)}
                  axisBottom={{
                    tickRotation: -45,
                    legend: 'Produkt',
                    legendPosition: 'middle',
                    legendOffset: 70,
                  }}
                  axisLeft={{
                    legend: 'Varianz (€)',
                    legendPosition: 'middle',
                    legendOffset: -60,
                    format: (value) => `€${formatNumber(value, 0)}`,
                  }}
                  labelFormat={(value) => `€${formatNumber(Number(value), 0)}`}
                  tooltip={({ indexValue, value }) => (
                    <Box
                      sx={{
                        background: colors.primary[400],
                        padding: '5px 10px',
                        border: `1px solid ${colors.grey[100]}`,
                      }}
                    >
                      <Typography variant="body2">{indexValue}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        €{formatNumber(value, 2)}
                      </Typography>
                    </Box>
                  )}
                  theme={{
                    axis: {
                      ticks: { text: { fill: colors.grey[100] } },
                      legend: { text: { fill: colors.grey[100] } },
                    },
                    tooltip: {
                      container: { background: colors.primary[400], color: colors.grey[100] },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Variance Distribution Histogram */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Varianz-Verteilung</Typography>
                <Tooltip title="Verteilung der Orders nach Höhe der Abweichung">
                  <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[300] }} />
                </Tooltip>
              </Box>
              <Box height="300px">
                <ResponsiveBar
                  data={distributionChartData}
                  keys={['count']}
                  indexBy="bucket"
                  margin={{ top: 10, right: 30, bottom: 60, left: 60 }}
                  padding={0.3}
                  colors={colors.blueAccent[500]}
                  axisBottom={{
                    legend: 'Varianz-Bereich',
                    legendPosition: 'middle',
                    legendOffset: 50,
                  }}
                  axisLeft={{
                    legend: 'Anzahl Orders',
                    legendPosition: 'middle',
                    legendOffset: -50,
                  }}
                  tooltip={({ indexValue, value }) => (
                    <Box
                      sx={{
                        background: colors.primary[400],
                        padding: '5px 10px',
                        border: `1px solid ${colors.grey[100]}`,
                      }}
                    >
                      <Typography variant="body2">{indexValue}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {value} Orders
                      </Typography>
                    </Box>
                  )}
                  theme={{
                    axis: {
                      ticks: { text: { fill: colors.grey[100] } },
                      legend: { text: { fill: colors.grey[100] } },
                    },
                    tooltip: {
                      container: { background: colors.primary[400], color: colors.grey[100] },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Product-Level Variance Table */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Varianz nach Produkt
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produkt</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Theoretisch</TableCell>
                  <TableCell align="right">Tatsächlich</TableCell>
                  <TableCell align="right">Varianz</TableCell>
                  <TableCell align="right">Varianz %</TableCell>
                  <TableCell align="right">Ø/Order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {by_product.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.product_name}</TableCell>
                    <TableCell align="right">{row.order_count}</TableCell>
                    <TableCell align="right">
                      €{formatNumber(parseFloat(row.theoretical_revenue), 2)}
                    </TableCell>
                    <TableCell align="right">
                      €{formatNumber(parseFloat(row.actual_revenue), 2)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                          color:
                            parseFloat(row.variance) < 0
                              ? colors.redAccent[500]
                              : colors.greenAccent[500],
                        }}
                      >
                        {parseFloat(row.variance) < 0 ? (
                          <TrendingDownIcon fontSize="small" />
                        ) : (
                          <TrendingUpIcon fontSize="small" />
                        )}
                        €{formatNumber(Math.abs(parseFloat(row.variance)), 2)}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          parseFloat(row.variance) < 0
                            ? colors.redAccent[500]
                            : colors.greenAccent[500],
                      }}
                    >
                      {parseFloat(row.variance_pct || '0').toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      €{formatNumber(Math.abs(parseFloat(row.avg_variance_per_order)), 2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Variance Orders Table */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Top Varianz Orders (Top 100)
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Bestellnummer</TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell align="right">Theoretisch</TableCell>
                  <TableCell align="right">Tatsächlich</TableCell>
                  <TableCell align="right">Varianz</TableCell>
                  <TableCell align="right">%</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell align="right">Tage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top_variance_orders.map((row, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{row.ordernumber}</TableCell>
                    <TableCell>{row.product_name}</TableCell>
                    <TableCell>{row.subscription_type}</TableCell>
                    <TableCell align="right">
                      €{formatNumber(parseFloat(row.theoretical_revenue), 2)}
                    </TableCell>
                    <TableCell align="right">
                      €{formatNumber(parseFloat(row.actual_revenue), 2)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                          color:
                            parseFloat(row.variance) < 0
                              ? colors.redAccent[500]
                              : colors.greenAccent[500],
                        }}
                      >
                        {parseFloat(row.variance) < 0 ? (
                          <TrendingDownIcon fontSize="small" />
                        ) : (
                          <TrendingUpIcon fontSize="small" />
                        )}
                        €{formatNumber(Math.abs(parseFloat(row.variance)), 2)}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          parseFloat(row.variance) < 0
                            ? colors.redAccent[500]
                            : colors.greenAccent[500],
                      }}
                    >
                      {parseFloat(row.variance_pct || '0').toFixed(1)}%
                    </TableCell>
                    <TableCell>{moment(row.created_on).format('DD.MM.YYYY')}</TableCell>
                    <TableCell align="right">{row.lifetime_days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {selectedOrderNumber && (
        <OrderDetailsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          ordernumber={selectedOrderNumber}
        />
      )}
    </Box>
  );
};

export default RevenueVariancePage;
